import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Send, Mail, Users, CalendarDays, ChevronLeft, ChevronRight, Eye, EyeOff } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

function monthKey(offset) {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() + offset);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function MonthlyDigestCard() {
  const qc = useQueryClient();
  const [offset, setOffset] = useState(0);
  const [showPreview, setShowPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const month = monthKey(offset);

  const preview = useQuery({
    queryKey: ["monthly-digest", month],
    queryFn: () => api.get(`/admin/monthly-digest/preview?month=${month}`).then((r) => r.data),
  });
  const d = preview.data;

  const saveSettings = async (patch) => {
    try {
      await api.post("/admin/monthly-digest/settings", { ...d.settings, ...patch });
      qc.invalidateQueries({ queryKey: ["monthly-digest"] });
      toast.success("Digest settings saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const sendNow = async () => {
    if (!d?.sendable) return;
    const list = (d.events || []).slice(0, 6).map((e) => `• ${e.date} — ${e.title}`).join("\n");
    const more = (d.events || []).length > 6 ? `\n…and ${d.events.length - 6} more` : "";
    if (!window.confirm(`Send the ${d.month_label} events digest to ${d.recipient_count ?? 0} member(s)?\n\nThis will include ${d.event_count} upcoming event(s):\n${list}${more}`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/monthly-digest/send", { month });
      if (data.ran) toast.success(`Digest sent to ${data.sent} member(s)${data.failed ? ` · ${data.failed} failed` : ""}`);
      else toast.error(data.reason === "no_upcoming_events" ? "No upcoming events — nothing was sent." : data.reason === "past_month_blocked" ? "Past months can't be broadcast." : data.reason === "resend_not_configured" ? "Connect Resend in Integrations first." : `Not sent: ${data.reason}`);
      qc.invalidateQueries({ queryKey: ["monthly-digest"] });
      qc.invalidateQueries({ queryKey: ["crm-campaigns"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <div className="eyw-card p-6" data-testid="monthly-digest-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-serif text-xl text-[#1A242B] flex items-center gap-2 mb-1"><CalendarDays size={18} className="text-[#D98E73]" /> Monthly events digest</h3>
          <p className="text-sm text-[#4A5D6B]">A warm "What's Happening This Month" email built from the live calendar, sent automatically to members.</p>
        </div>
        <label className="flex items-center gap-2 text-sm text-[#1A242B] cursor-pointer">
          <input type="checkbox" data-testid="digest-enabled-toggle" checked={d?.settings?.enabled ?? true}
            onChange={(e) => saveSettings({ enabled: e.target.checked })} className="h-4 w-4 accent-[#144F75]" disabled={!d} />
          Auto-send
        </label>
      </div>

      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-4 text-sm text-[#4A5D6B]">
        <span className="flex items-center gap-1.5">
          <button onClick={() => setOffset((o) => o - 1)} data-testid="digest-prev-month" aria-label="Previous month" className="grid place-items-center h-7 w-7 rounded-full border border-[#E5E0D8] text-[#144F75] hover:bg-[#F2EEE7]"><ChevronLeft size={14} /></button>
          <span data-testid="digest-month-label" className="font-medium text-[#1A242B] min-w-[120px] text-center">{d?.month_label || month}</span>
          <button onClick={() => setOffset((o) => o + 1)} data-testid="digest-next-month" aria-label="Next month" className="grid place-items-center h-7 w-7 rounded-full border border-[#E5E0D8] text-[#144F75] hover:bg-[#F2EEE7]"><ChevronRight size={14} /></button>
        </span>
        <span data-testid="digest-event-count">{preview.isLoading ? "…" : `${d?.event_count ?? 0} upcoming event(s)`}</span>
        <span className="flex items-center gap-1" data-testid="digest-recipient-count"><Users size={13} /> {d?.recipient_count ?? 0} member(s)</span>
        {d?.already_sent && <span className="text-[#5B8A72] text-xs font-medium" data-testid="digest-sent-badge">✓ Sent {String(d.last_sent_at || "").slice(0, 10)}</span>}
        {d?.block_reason === "past_month" && <span className="text-[#B45151] text-xs font-medium" data-testid="digest-past-blocked">Past month — sending disabled</span>}
        {d && !d.resend_configured && <span className="text-[#B5794E] text-xs" data-testid="digest-resend-warning">Connect Resend in Integrations to deliver emails</span>}
      </div>

      <div className="flex flex-wrap items-center gap-3 mt-4 text-sm">
        <label className="flex items-center gap-2 text-[#4A5D6B]">Send on day
          <select value={d?.settings?.day ?? 1} onChange={(e) => saveSettings({ day: Number(e.target.value) })} data-testid="digest-day-select" className="eyw-input !py-1.5 !px-2 text-sm" disabled={!d}>
            {Array.from({ length: 28 }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-2 text-[#4A5D6B]">at
          <select value={d?.settings?.hour ?? 9} onChange={(e) => saveSettings({ hour: Number(e.target.value) })} data-testid="digest-hour-select" className="eyw-input !py-1.5 !px-2 text-sm" disabled={!d}>
            {Array.from({ length: 24 }, (_, i) => i).map((n) => <option key={n} value={n}>{String(n).padStart(2, "0")}:00 UTC</option>)}
          </select>
        </label>
        <span className="flex-1" />
        <button onClick={() => setShowPreview((s) => !s)} data-testid="digest-preview-toggle" className="eyw-btn-outline !py-2 text-sm">
          {showPreview ? <EyeOff size={15} /> : <Eye size={15} />} {showPreview ? "Hide preview" : "Preview email"}
        </button>
        <button onClick={sendNow} disabled={sending || !d?.sendable} data-testid="digest-send-now" className="eyw-btn !py-2 text-sm disabled:opacity-60">
          {sending ? <Loader2 className="animate-spin" size={15} /> : <Send size={15} />} Send now
        </button>
      </div>

      {showPreview && d?.html && (
        <iframe title="Digest preview" srcDoc={d.html} sandbox="" data-testid="digest-preview-frame"
          className="w-full h-[520px] mt-5 rounded-xl border border-[#E5E0D8] bg-[#F9F7F3]" />
      )}
    </div>
  );
}

const SEGMENT_LABELS = {
  all: "Everyone (all accounts)",
  members: "Paying members (community, enhancer, staff)",
  community: "Community members",
  enhancer: "Enhancers",
  discovery: "Discovery members",
};

export default function CrmPanel() {
  const qc = useQueryClient();
  const [segment, setSegment] = useState("all");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  const audience = useQuery({
    queryKey: ["crm-audience", segment],
    queryFn: () => api.get(`/admin/crm/audience?segment=${segment}`).then((r) => r.data),
  });
  const campaigns = useQuery({
    queryKey: ["crm-campaigns"],
    queryFn: () => api.get("/admin/crm/campaigns").then((r) => r.data),
  });

  const send = async () => {
    if (!subject.trim() || !body.trim()) { toast.error("Add a subject and message"); return; }
    const count = audience.data?.count ?? 0;
    if (!window.confirm(`Send "${subject}" to ${count} recipient(s) in "${SEGMENT_LABELS[segment]}"?`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/crm/broadcast", { subject, body, segment });
      toast.success(`Sent to ${data.sent} · ${data.failed} failed`);
      setSubject(""); setBody("");
      qc.invalidateQueries({ queryKey: ["crm-campaigns"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <div className="space-y-6" data-testid="crm-panel">
      <MonthlyDigestCard />
      <div className="eyw-card p-6">
        <h3 className="font-serif text-xl text-[#1A242B] flex items-center gap-2 mb-1"><Mail size={18} className="text-[#144F75]" /> Broadcast email</h3>
        <p className="text-sm text-[#4A5D6B] mb-5">Send an update to your members. People who opted out of email are automatically excluded. Delivery uses Resend (verify a domain to reach all recipients).</p>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-[#4A5D6B] block mb-1">Audience</label>
            <select value={segment} onChange={(e) => setSegment(e.target.value)} data-testid="crm-segment" className="eyw-input w-full">
              {Object.entries(SEGMENT_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <p className="text-[11px] text-[#8FA89B] mt-1 flex items-center gap-1" data-testid="crm-audience-count">
              <Users size={12} /> {audience.isLoading ? "…" : `${audience.data?.count ?? 0} recipient(s)`}
            </p>
          </div>
          <div>
            <label className="text-xs text-[#4A5D6B] block mb-1">Subject</label>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="crm-subject" placeholder="A note from Enhancing Your World" className="eyw-input w-full" />
          </div>
        </div>
        <div className="mt-4">
          <label className="text-xs text-[#4A5D6B] block mb-1">Message</label>
          <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={8} data-testid="crm-body"
            placeholder={"Write your message here.\n\nEach line becomes a paragraph. It's wrapped in the Enhancing Your World email template automatically."} className="eyw-input w-full" />
        </div>
        <button onClick={send} disabled={sending} data-testid="crm-send" className="eyw-btn mt-4 disabled:opacity-60">
          {sending ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />} Send broadcast
        </button>
      </div>

      <div className="eyw-card p-6">
        <h3 className="font-serif text-lg text-[#1A242B] mb-3">Recent broadcasts</h3>
        <div className="space-y-2" data-testid="crm-history">
          {(campaigns.data || []).map((c) => (
            <div key={c.id} className="flex items-center justify-between bg-white rounded-lg px-4 py-3 border border-[#E5E0D8] text-sm">
              <div>
                <span className="text-[#1A242B] font-medium">{c.subject}</span>
                <span className="text-[#8FA89B]"> · {SEGMENT_LABELS[c.segment] || c.segment}</span>
                <div className="text-xs text-[#8FA89B]">{(c.created_at || "").slice(0, 16).replace("T", " ")}</div>
              </div>
              <span className="text-xs text-[#4A5D6B] whitespace-nowrap">{c.sent}/{c.recipients} sent{c.failed ? ` · ${c.failed} failed` : ""}</span>
            </div>
          ))}
          {(campaigns.data || []).length === 0 && <p className="text-sm text-[#8FA89B]">No broadcasts sent yet.</p>}
        </div>
      </div>
    </div>
  );
}
